import * as PropTypes from 'prop-types';
import * as React from 'react';
import { APIRequest, MobileDetection } from '@reverbdotcom/discovery-ui';
import EditableCreditCard from './editable_credit_card';
import UrlHelpers from '../shared/url_helpers';
import UiUtil from '../utils/ui';
import CreditCardLoadingPlaceholder from './credit_card_loading_placeholder';
import CreditCards from './credit_cards';
import { blankCreditCard } from './credit_card';

class CreditCardList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      creditCardsFetched: false,
      creditCards: new CreditCards([]),
      countriesForSelect: [],
      addingNewCreditCard: false,
      primaryCardId: props.primaryCardId,
      initialSelectedCreditCardId: null,
      nowEditingCreditCardId: null,
    };
  }

  componentDidMount() {
    this.fetchCountriesForSelect();
    this.fetchCreditCards();
  }

  handleFormSave() {
    this.setState({
      initialSelectedCreditCardId: null,
      creditCards: new CreditCards([]),
      creditCardsFetched: false,
    });
    if (MobileDetection.isMobileScreenWidth()) {
      UiUtil.scrollToElement(this.creditCardListRootRef);
    }
    this.fetchCreditCards();
  }

  fetchCreditCards() {
    APIRequest.V3.get(UrlHelpers.myCreditCardsIndexPath).done((response) => {
      this.setState({
        creditCards: new CreditCards(response.credit_cards),
        creditCardsFetched: true,
        addingNewCreditCard: false,
      });
    });
  }

  fetchCountriesForSelect() {
    APIRequest.V3.get(UrlHelpers.countriesPath).done((response) => {
      this.setState({
        countriesForSelect: response.countries,
      });
    });
  }

  isLoading() {
    // the countries list has not yet loaded from the API
    return (this.state.countriesForSelect.length === 0 || !this.state.creditCardsFetched);
  }

  isSelected(creditCard, index) {
    if (this.state.initialSelectedCreditCardId && this.selectedCreditCard()) {
      return creditCard.creditCardId === this.state.initialSelectedCreditCardId;
    }

    // When creating a new credit card, the blank form is selected,
    // and none of the persisted CCs are selected.
    if (this.isCreatingCreditCard()) {
      return false;
    }

    return index === 0;
  }

  isEditing(creditCard) {
    return creditCard.creditCardId === this.state.nowEditingCreditCardId;
  }

  selectedCreditCard() {
    return this.state.creditCards.findById(this.state.initialSelectedCreditCardId);
  }

  nonSelectedCreditCards() {
    return this.state.creditCards.nonSelectedCreditCards(this.state.initialSelectedCreditCardId);
  }

  creditCardsInOrder() {
    return this.state.creditCards.creditCardsInOrder(this.state.initialSelectedCreditCardId);
  }

  // (a workaround to have the proper card to appear selected/highlighted)
  // boolean flags are added to the key to force rerendering of the credit card
  keyForEditableCreditCard(creditCard, index) {
    return `${creditCard.creditCardId}-${this.isCreatingCreditCard()}-${this.isSelected(creditCard, index)}`;
  }

  persistedCreditCards() {
    return (
      this.creditCardsInOrder().map((creditCard, index) =>
        (<EditableCreditCard
          key={this.keyForEditableCreditCard(creditCard, index)}
          creditCard={creditCard}
          countriesForSelect={this.state.countriesForSelect}
          formObjectName={this.props.formObjectName}
          selected={this.isSelected(creditCard, index)}
          primaryCardId={this.state.primaryCardId}
          editing={this.isEditing(creditCard)}
          handleUpdate={() => this.handleFormSave()}
          updatePrimaryCard={cardId => this.setState({ primaryCardId: cardId })}
          handleEditing={(creditCardId) => {
            this.setState({ nowEditingCreditCardId: creditCardId, addingNewCreditCard: false });
          }}
        />))
    );
  }

  blankCreditCardForm() {
    if (this.isCreatingCreditCard()) {
      return (
        <EditableCreditCard
          countriesForSelect={this.state.countriesForSelect}
          handleUpdate={() => this.handleFormSave()}
          creditCard={blankCreditCard()}
          formObjectName={this.props.formObjectName}
          handleEditing={creditCardId => this.setState({ nowEditingCreditCardId: creditCardId })}
          primaryCardId={this.state.primaryCardId}
          selected
          editing
        />
      );
    }
    return null;
  }

  hasCreditCards() {
    return this.state.creditCards.hasCards;
  }

  isCreatingCreditCard() {
    return this.state.addingNewCreditCard;
  }

  handleAddNewCreditCardClick() {
    this.setState({
      addingNewCreditCard: true,
      nowEditingCreditCardId: null,
    });
  }

  addCreditCardButton(buttonClasses) {
    return (
      <div className="g-col-6 g-col-mobile-12 mobile-mb-1">
        <a
          id="add_new_credit_card"
          className={buttonClasses}
          onClick={() => this.handleAddNewCreditCardClick()}
        >
          {I18n.t('js.creditCards.creditCardList.addNewCard')}
        </a>
      </div>
    );
  }

  cannotAddNewCardWarning() {
    return (
      <div className="g-col-12 g-col-mobile-12 mobile-mb-1">
        <div className="alert-box alert-box--blue scaling-mb-2">
          {I18n.t('js.creditCards.creditCardList.cannotAddNewCard')}
        </div>
      </div>
    );
  }

  addCreditCardEmptyState() {
    return (
      <div className="g-col-6 g-col-mobile-12 mobile-mb-1">
        <div className="align-center">
          <p className="mtb-1 muted">
            {I18n.t('js.creditCards.creditCardList.noCardsOnFile')}
          </p>
          {this.addCreditCardButton('button button--orange')}
        </div>
      </div>
    );
  }

  addNewCreditCardAction() {
    if (this.isCreatingCreditCard()) { return null; }

    if (!this.props.belowCreditCardThreshold) {
      return this.cannotAddNewCardWarning();
    }

    if (this.hasCreditCards()) {
      return this.addCreditCardButton('button button--blue mobile-width-100');
    }

    if (!this.hasCreditCards()) {
      return this.addCreditCardEmptyState();
    }

    return null;
  }

  render() {
    if (this.isLoading()) {
      return (
        <div ref={(e) => { this.creditCardListRootRef = e; }}>
          <CreditCardLoadingPlaceholder />
        </div>
      );
    }

    return (
      <div ref={(e) => { this.creditCardListRootRef = e; }}>
        <ul>
          {this.persistedCreditCards()}
          {this.blankCreditCardForm()}
        </ul>
        <div className="g-container">
          {this.addNewCreditCardAction()}
        </div>
      </div>
    );
  }
}

CreditCardList.propTypes = {
  formObjectName: PropTypes.string.isRequired,
  primaryCardId: PropTypes.string.isRequired,
  belowCreditCardThreshold: PropTypes.bool.isRequired,
};

export default CreditCardList;



// WEBPACK FOOTER //
// ./javascripts/components/credit_cards/credit_card_list.jsx